import _ from 'lodash';
import React, { useRef } from 'react';
import styled from 'styled-components';
import { Checkbox, Field, Form, HelpTooltip, InvoiceVariablesDialog, Radio, Tooltip } from '~/components';
import ReadImage from '~/components/read-only/ReadImage';
import ReadTextbox from '~/components/read-only/ReadTextbox';
import { useConfirmation, useWorkspace } from '~/contexts';
import { useAuth, useFeatures } from '~/hooks';
import { colors } from '~/styles';
import SettingsFormSection from '../../SettingsFormSection';
import LabelTooltip from './LabelTooltip';

const ControlLabel = styled.p`
  display: flex;
  color: ${colors.grey75};

  &:not(:first-child) {
    margin-top: 1rem;
  }
`;

const Checkboxes = styled.div`
  display: flex;
  flex-wrap: wrap;
  margin: -0.375rem -0.75rem;

  > label {
    margin: 0.375rem 0.75rem;
  }
`;

const VariablesContainer = styled.div`
  margin-top: 1rem;
  display: flex;
  flex-direction: column;

  > a {
    margin-left: auto;
  }
`;

const StyledLink = styled.a``;

const taxableItems = {
  services: 'Services',
  expenses: 'Expenses',
  products: 'Products',
  other_items: 'Other Items',
};

export default function InvoiceDetails({ formik }) {
  const confirmation = useConfirmation();
  const features = useFeatures();
  const auth = useAuth();
  const { workspace } = useWorkspace();
  // The whole section is `billing`, so a role without it reads the stored invoice details instead of editing.
  const readOnly = !auth.projects.sections.billing;

  const invoiceSubjectRef = useRef(null);
  const invoiceNotesRef = useRef(null);
  const invoiceEmailSubjectRef = useRef(null);
  const invoiceEmailBodyRef = useRef(null);

  const { client } = formik.values;

  const handleEmailChange = (event) => {
    const value = event.target.value;
    formik.setFieldValue('email', value);

    const values = value.split(/[\s,;]+/).filter(Boolean);
    formik.setFieldValue('emails', values);
  };

  const handleCcEmailChange = (event) => {
    const value = event.target.value;
    formik.setFieldValue('ccEmail', value);

    const values = value.split(/[\s,;]+/).filter(Boolean);
    formik.setFieldValue('ccEmails', values);
  };

  const processEmailErrors = (errors) => {
    if (!errors || !errors.map) {
      return errors;
    }
    return errors.map((error) => {
      if (typeof error !== 'string') {
        return error;
      }
      return error.replace(/emails\[(\d+)\]/, (match, p1) => `To Email Address #${parseInt(p1) + 1}`);
    });
  };

  const processCcEmailErrors = (errors) => {
    if (!errors || !errors.map) {
      return errors;
    }
    return errors.map((error) => {
      if (typeof error !== 'string') {
        return error;
      }
      return error.replace(/ccEmails\[(\d+)\]/, (match, p1) => `Cc Email Address #${parseInt(p1) + 1}`);
    });
  };

  const handleTaxableItemsChange = (name) => {
    let fields;
    if (formik.values.invoiceTaxableItems.some((v) => v === name)) {
      fields = formik.values.invoiceTaxableItems.filter((v) => v !== name);
    } else {
      fields = [...formik.values.invoiceTaxableItems, name];
    }
    formik.setFieldValue('invoiceTaxableItems', fields);
  };

  const getRefByName = (name) => {
    switch (name) {
      case 'invoiceSubject':
        return invoiceSubjectRef;
      case 'invoiceNotes':
        return invoiceNotesRef;
      case 'invoiceEmailSubject':
        return invoiceEmailSubjectRef;
      case 'invoiceEmailBody':
        return invoiceEmailBodyRef;
      default:
        return null;
    }
  };

  const handleOpenInvoiceVariablesDialog = async (name) => {
    await confirmation.prompt((resolve) => (
      <InvoiceVariablesDialog
        onClose={() => resolve(true)}
        onSaved={(variables) => {
          const ref = getRefByName(name);

          let selectionStart = ref.current.selectionStart;
          if (!formik.touched[name]) {
            selectionStart = formik.values[name].length;
          }

          const updatedValue =
            formik.values[name].slice(0, selectionStart) +
            variables.join(' ') +
            formik.values[name].slice(selectionStart);

          formik.setFieldValue(name, updatedValue);
          resolve(true);
        }}
      />
    ));
  };

  if (!client || client.isInternal) return null;

  return (
    <SettingsFormSection title="Invoice Details">
      <Form.Control>
        <Field.RadioGroup name="invoiceDetailsSource" disabled={readOnly}>
          <LabelTooltip>
            <Radio value="client" label="Use client defaults" disabled={readOnly} />
            {client.invoiceDetailsSource === 'workspace' && (
              <HelpTooltip message="This client uses the workspace invoice details." style={{ marginLeft: '0.5rem' }} />
            )}
            {client.invoiceDetailsSource === 'business_unit' && (
              <HelpTooltip
                message="This client uses the business unit invoice details."
                style={{ marginLeft: '0.5rem' }}
              />
            )}
          </LabelTooltip>
          {features.businessUnits && (
            <Tooltip
              message={
                formik.values.businessUnit ? undefined : 'A Business Unit has not been selected for this project.'
              }>
              <Radio
                value="business_unit"
                label="Use Business Unit defaults"
                disabled={readOnly || !formik.values.businessUnit}
              />
            </Tooltip>
          )}
          <LabelTooltip>
            <Radio value="custom" label="Use custom invoice values" disabled={readOnly} />
            <HelpTooltip
              message="This setting will use the issuing company name, street address, and logo that are configured at the client level."
              placement="right"
              style={{ marginLeft: '0.5rem' }}
            />
          </LabelTooltip>
        </Field.RadioGroup>
      </Form.Control>

      {(() => {
        switch (formik.values.invoiceDetailsSource) {
          case 'client':
            switch (formik.values.client.invoiceDetailsSource) {
              case 'workspace':
                return (
                  <>
                    <Form.Control>
                      <ReadTextbox label="Issuing Company Name" value={workspace.companyName} />
                    </Form.Control>
                    <Form.Control>
                      <ReadTextbox label="Issuing Company Street Address" textarea value={workspace.streetAddress} />
                    </Form.Control>
                    {workspace.includeInvoiceLogo && (
                      <Form.Control>
                        <ReadImage
                          label="Issuing Company Logo"
                          value={workspace.invoiceImageUrl ?? workspace.imageUrl}
                        />
                      </Form.Control>
                    )}
                    <Form.Control help="Can contain multiple emails separated by a comma, semicolon, or space.">
                      <ReadTextbox label="To Email Address" value={client.emails.join(', ')} />
                    </Form.Control>
                    <Form.Control help="Can contain multiple emails separated by a comma, semicolon, or space.">
                      <ReadTextbox label="Cc Email Address" value={client.ccEmails.join(', ')} />
                    </Form.Control>
                    <Form.Control>
                      <ReadTextbox label="Client's Invoice Street Address" textarea value={client.streetAddress} />
                    </Form.Control>
                    <Form.Control>
                      <Field.PaymentTermSelect
                        name="invoicePaymentTerm"
                        placeholder="Payment Terms"
                        value={workspace.invoicePaymentTerm}
                        disabled
                        clearable={false}
                      />
                    </Form.Control>
                    <Form.Control>
                      <Field.TaxRateSelect
                        name="invoiceTaxRate"
                        placeholder="Tax Rate"
                        clearable={false}
                        disabled
                        value={workspace.invoiceTaxRate}
                      />
                    </Form.Control>
                    <ControlLabel>The following items are taxable by default:</ControlLabel>
                    <Form.Control>
                      <Checkboxes>
                        {_.map(taxableItems, (value, key) => (
                          <Checkbox
                            key={key}
                            label={value}
                            checked={workspace.invoiceTaxableItems.some((v) => v === key)}
                            disabled
                          />
                        ))}
                      </Checkboxes>
                    </Form.Control>
                    <Form.Control>
                      <ReadTextbox label="Invoice Subject" value={workspace.invoiceSubject} />
                    </Form.Control>
                    <Form.Control>
                      <ReadTextbox label="Invoice Note" textarea value={workspace.invoiceNotes} />
                    </Form.Control>
                    <Form.Control>
                      <ReadTextbox label="Invoice Email Subject" value={workspace.invoiceEmailSubject} />
                    </Form.Control>
                    <Form.Control>
                      <ReadTextbox label="Invoice Email Body" textarea value={workspace.invoiceEmailBody} />
                    </Form.Control>
                  </>
                );
              case 'business_unit':
                return (
                  <>
                    <Form.Control>
                      <ReadTextbox label="Issuing Company Name" value={client.businessUnit.companyName} />
                    </Form.Control>
                    <Form.Control>
                      <ReadTextbox
                        label="Issuing Company Street Address"
                        textarea
                        value={client.businessUnit.streetAddress}
                      />
                    </Form.Control>
                    {workspace.includeInvoiceLogo && (
                      <Form.Control>
                        <ReadImage label="Issuing Company Logo" value={client.businessUnit.invoiceImageUrl} />
                      </Form.Control>
                    )}
                    <Form.Control help="Can contain multiple emails separated by a comma, semicolon, or space.">
                      <ReadTextbox label="To Email Address" value={client.emails.join(', ')} />
                    </Form.Control>
                    <Form.Control help="Can contain multiple emails separated by a comma, semicolon, or space.">
                      <ReadTextbox label="Cc Email Address" value={client.ccEmails.join(', ')} />
                    </Form.Control>
                    <Form.Control>
                      <ReadTextbox label="Client's Invoice Street Address" textarea value={client.streetAddress} />
                    </Form.Control>
                    <Form.Control>
                      <Field.PaymentTermSelect
                        name="invoicePaymentTerm"
                        placeholder="Payment Terms"
                        value={client.businessUnit.invoicePaymentTerm}
                        disabled
                        clearable={false}
                      />
                    </Form.Control>
                    <Form.Control>
                      <Field.TaxRateSelect
                        name="invoiceTaxRate"
                        placeholder="Tax Rate"
                        clearable={false}
                        disabled
                        value={client.businessUnit.invoiceTaxRate}
                      />
                    </Form.Control>
                    <ControlLabel>The following items are taxable by default:</ControlLabel>
                    <Form.Control>
                      <Checkboxes>
                        {_.map(taxableItems, (value, key) => (
                          <Checkbox
                            key={key}
                            label={value}
                            checked={client.businessUnit.invoiceTaxableItems.some((v) => v === key)}
                            disabled
                          />
                        ))}
                      </Checkboxes>
                    </Form.Control>
                    <Form.Control>
                      <ReadTextbox label="Invoice Subject" value={client.businessUnit.invoiceSubject} />
                    </Form.Control>
                    <Form.Control>
                      <ReadTextbox label="Invoice Note" textarea value={client.businessUnit.invoiceNotes} />
                    </Form.Control>
                    <Form.Control>
                      <ReadTextbox label="Invoice Email Subject" value={client.businessUnit.invoiceEmailSubject} />
                    </Form.Control>
                    <Form.Control>
                      <ReadTextbox label="Invoice Email Body" textarea value={client.businessUnit.invoiceEmailBody} />
                    </Form.Control>
                  </>
                );
              case 'custom':
                return (
                  <>
                    <Form.Control>
                      <ReadTextbox label="Issuing Company Name" value={workspace.companyName} />
                    </Form.Control>
                    <Form.Control>
                      <ReadTextbox label="Issuing Company Street Address" textarea value={workspace.streetAddress} />
                    </Form.Control>
                    {workspace.includeInvoiceLogo && (
                      <Form.Control>
                        <ReadImage
                          label="Issuing Company Logo"
                          value={workspace.invoiceImageUrl ?? workspace.imageUrl}
                        />
                      </Form.Control>
                    )}
                    <Form.Control help="Can contain multiple emails separated by a comma, semicolon, or space.">
                      <ReadTextbox label="To Email Address" value={client.emails.join(', ')} />
                    </Form.Control>
                    <Form.Control help="Can contain multiple emails separated by a comma, semicolon, or space.">
                      <ReadTextbox label="Cc Email Address" value={client.ccEmails.join(', ')} />
                    </Form.Control>
                    <Form.Control>
                      <ReadTextbox label="Client's Invoice Street Address" textarea value={client.streetAddress} />
                    </Form.Control>
                    <Form.Control>
                      <Field.PaymentTermSelect
                        name="invoicePaymentTerm"
                        placeholder="Payment Terms"
                        value={client.invoicePaymentTerm}
                        disabled
                        clearable={false}
                      />
                    </Form.Control>
                    <Form.Control>
                      <Field.TaxRateSelect
                        name="invoiceTaxRate"
                        placeholder="Tax Rate"
                        clearable={false}
                        disabled
                        value={client.invoiceTaxRate}
                      />
                    </Form.Control>
                    <ControlLabel>The following items are taxable by default:</ControlLabel>
                    <Form.Control>
                      <Checkboxes>
                        {_.map(taxableItems, (value, key) => (
                          <Checkbox
                            key={key}
                            label={value}
                            checked={client.invoiceTaxableItems.some((v) => v === key)}
                            disabled
                          />
                        ))}
                      </Checkboxes>
                    </Form.Control>
                    <Form.Control>
                      <ReadTextbox label="Invoice Subject" value={client.invoiceSubject} />
                    </Form.Control>
                    <Form.Control>
                      <ReadTextbox label="Invoice Note" textarea value={client.invoiceNotes} />
                    </Form.Control>
                    <Form.Control>
                      <ReadTextbox label="Invoice Email Subject" value={client.invoiceEmailSubject} />
                    </Form.Control>
                    <Form.Control>
                      <ReadTextbox label="Invoice Email Body" textarea value={client.invoiceEmailBody} />
                    </Form.Control>
                  </>
                );
            }
            break;
          case 'business_unit':
            return (
              <>
                <Form.Control>
                  <ReadTextbox label="Issuing Company Name" value={formik.values.businessUnit.companyName} />
                </Form.Control>
                <Form.Control>
                  <ReadTextbox
                    label="Issuing Company Street Address"
                    textarea
                    value={formik.values.businessUnit.streetAddress}
                  />
                </Form.Control>
                {workspace.includeInvoiceLogo && (
                  <Form.Control>
                    <ReadImage label="Issuing Company Logo" value={formik.values.businessUnit.invoiceImageUrl} />
                  </Form.Control>
                )}
                <Form.Control help="Can contain multiple emails separated by a comma, semicolon, or space.">
                  <Field.Text
                    name="emails"
                    value={formik.values.email}
                    placeholder="To Email Address"
                    onChange={handleEmailChange}
                    onProcessErrors={processEmailErrors}
                    disabled={readOnly}
                  />
                </Form.Control>
                <Form.Control help="Can contain multiple emails separated by a comma, semicolon, or space.">
                  <Field.Text
                    name="ccEmails"
                    value={formik.values.ccEmail}
                    placeholder="Cc Email Address"
                    onChange={handleCcEmailChange}
                    onProcessErrors={processCcEmailErrors}
                    disabled={readOnly}
                  />
                </Form.Control>
                <Form.Control>
                  <Form.Control>
                    <Field.TextArea
                      name="streetAddress"
                      placeholder="Client's Invoice Street Address"
                      maxLength={5000}
                      rows={4}
                      disabled={readOnly}
                    />
                  </Form.Control>
                </Form.Control>
                <Form.Control>
                  <Field.PaymentTermSelect
                    name="invoicePaymentTerm"
                    placeholder="Payment Terms"
                    value={formik.values.businessUnit.invoicePaymentTerm}
                    disabled
                    clearable={false}
                  />
                </Form.Control>
                <Form.Control>
                  <Field.TaxRateSelect
                    name="invoiceTaxRate"
                    placeholder="Tax Rate"
                    clearable={false}
                    disabled
                    value={formik.values.businessUnit.invoiceTaxRate}
                  />
                </Form.Control>
                <ControlLabel>The following items are taxable by default:</ControlLabel>
                <Form.Control>
                  <Checkboxes>
                    {_.map(taxableItems, (value, key) => (
                      <Checkbox
                        key={key}
                        label={value}
                        checked={formik.values.businessUnit.invoiceTaxableItems.some((v) => v === key)}
                        disabled
                      />
                    ))}
                  </Checkboxes>
                </Form.Control>
                <Form.Control>
                  <ReadTextbox label="Invoice Subject" value={formik.values.businessUnit.invoiceSubject} />
                </Form.Control>
                <Form.Control>
                  <ReadTextbox label="Invoice Note" textarea value={formik.values.businessUnit.invoiceNotes} />
                </Form.Control>
                <Form.Control>
                  <ReadTextbox label="Invoice Email Subject" value={formik.values.businessUnit.invoiceEmailSubject} />
                </Form.Control>
                <Form.Control>
                  <ReadTextbox
                    label="Invoice Email Body"
                    textarea
                    value={formik.values.businessUnit.invoiceEmailBody}
                  />
                </Form.Control>
              </>
            );
          case 'custom':
            return (
              <>
                <Form.Control>
                  <ReadTextbox
                    label="Issuing Company Name"
                    value={
                      client.invoiceDetailsSource === 'business_unit'
                        ? client.businessUnit.companyName
                        : workspace.companyName
                    }
                  />
                </Form.Control>
                <Form.Control>
                  <ReadTextbox
                    label="Issuing Company Street Address"
                    textarea
                    value={
                      client.invoiceDetailsSource === 'business_unit'
                        ? client.businessUnit.streetAddress
                        : workspace.streetAddress
                    }
                  />
                </Form.Control>
                {workspace.includeInvoiceLogo && (
                  <Form.Control>
                    <ReadImage
                      label="Issuing Company Logo"
                      value={
                        client.invoiceDetailsSource === 'business_unit'
                          ? client.businessUnit.invoiceImageUrl
                          : (workspace.invoiceImageUrl ?? workspace.imageUrl)
                      }
                    />
                  </Form.Control>
                )}
                <Form.Control help="Can contain multiple emails separated by a comma, semicolon, or space.">
                  <Field.Text
                    name="emails"
                    value={formik.values.email}
                    placeholder="To Email Address"
                    onChange={handleEmailChange}
                    onProcessErrors={processEmailErrors}
                    disabled={readOnly}
                  />
                </Form.Control>
                <Form.Control help="Can contain multiple emails separated by a comma, semicolon, or space.">
                  <Field.Text
                    name="ccEmails"
                    value={formik.values.ccEmail}
                    placeholder="Cc Email Address"
                    onChange={handleCcEmailChange}
                    onProcessErrors={processCcEmailErrors}
                    disabled={readOnly}
                  />
                </Form.Control>
                <Form.Control>
                  <Field.TextArea
                    name="streetAddress"
                    placeholder="Client's Invoice Street Address"
                    maxLength={5000}
                    rows={4}
                    disabled={readOnly}
                  />
                </Form.Control>
                <Form.Control>
                  <Field.PaymentTermSelect name="invoicePaymentTerm" placeholder="Payment Terms" disabled={readOnly} />
                </Form.Control>
                <Form.Control>
                  <Field.TaxRateSelect name="invoiceTaxRate" placeholder="Tax Rate" disabled={readOnly} />
                </Form.Control>
                <ControlLabel>The following items are taxable by default:</ControlLabel>
                <Form.Control>
                  <Checkboxes>
                    {_.map(taxableItems, (value, key) => (
                      <Checkbox
                        key={key}
                        label={value}
                        checked={formik.values.invoiceTaxableItems.some((v) => v === key)}
                        onChange={() => handleTaxableItemsChange(key)}
                        disabled={readOnly}
                      />
                    ))}
                  </Checkboxes>
                </Form.Control>
                <VariablesContainer>
                  {!readOnly && (
                    <StyledLink onClick={() => handleOpenInvoiceVariablesDialog('invoiceSubject')}>
                      Insert Variables
                    </StyledLink>
                  )}
                  <Form.Control>
                    <Field.Text
                      ref={invoiceSubjectRef}
                      name="invoiceSubject"
                      placeholder="Invoice Subject"
                      maxLength={255}
                      disabled={readOnly}
                    />
                  </Form.Control>
                </VariablesContainer>
                <VariablesContainer>
                  {!readOnly && (
                    <StyledLink onClick={() => handleOpenInvoiceVariablesDialog('invoiceNotes')}>
                      Insert Variables
                    </StyledLink>
                  )}
                  <Form.Control>
                    <Field.TextArea
                      ref={invoiceNotesRef}
                      name="invoiceNotes"
                      placeholder="Invoice Note"
                      maxLength={5000}
                      disabled={readOnly}
                    />
                  </Form.Control>
                </VariablesContainer>
                <VariablesContainer>
                  {!readOnly && (
                    <StyledLink onClick={() => handleOpenInvoiceVariablesDialog('invoiceEmailSubject')}>
                      Insert Variables
                    </StyledLink>
                  )}
                  <Form.Control>
                    <Field.Text
                      ref={invoiceEmailSubjectRef}
                      name="invoiceEmailSubject"
                      placeholder="Invoice Email Subject"
                      maxLength={255}
                      disabled={readOnly}
                    />
                  </Form.Control>
                </VariablesContainer>
                <VariablesContainer>
                  {!readOnly && (
                    <StyledLink onClick={() => handleOpenInvoiceVariablesDialog('invoiceEmailBody')}>
                      Insert Variables
                    </StyledLink>
                  )}
                  <Form.Control>
                    <Field.TextArea
                      ref={invoiceEmailBodyRef}
                      name="invoiceEmailBody"
                      placeholder="Invoice Email Body"
                      maxLength={5000}
                      disabled={readOnly}
                    />
                  </Form.Control>
                </VariablesContainer>
              </>
            );
        }
      })()}
    </SettingsFormSection>
  );
}
